import React from 'react';
import { Link } from 'react-router-dom';
import { 
    Calendar, Clock, Users, Share2, Edit, Trash2, BookOpen, PlusCircle, 
    AlertTriangle, CheckCheck, Hourglass, Swords
} from 'lucide-react';
import { useGameSessions } from '../hooks/useGameSessions'; // Importa o hook
import PendingSharedSessions from '../../../components/PendingSharedSessions'; // Importa o componente de sessões pendentes
import { CombinedGameSession, GameSession, GameSessionFilters } from '../../../lib/game/sessionService';
import { UserGame, Game } from '../../../lib/game/collectionService';

interface SessionHistoryViewProps {
  userId: string;
  playedGames: UserGame[]; // Necessário para o filtro de jogos
  sessionHook: ReturnType<typeof useGameSessions>;
  onEditSession: (session: GameSession, game?: UserGame['game']) => void;
  onAddSessionClick: () => void; // Para abrir o modal de adição geral (sem jogo pré-selecionado)
}

// Função helper para o tooltip do status de compartilhamento
const getShareStatusTooltip = (status: CombinedGameSession['share_status']): string => {
  switch (status) {
    case 'pending': return 'Compartilhamento pendente';
    case 'accepted_by_some': return 'Aceito por alguns jogadores';
    case 'accepted_by_all': return 'Aceito por todos os jogadores';
    default: return '';
  }
};

const SessionHistoryView: React.FC<SessionHistoryViewProps> = ({ 
    userId, 
    playedGames, 
    sessionHook, 
    onEditSession,
    onAddSessionClick
}) => {
  const {
    gameSessions,
    sessionsCount,
    sessionsLoading,
    sessionsPage,
    sessionsFilters,
    error,
    sessionsTotalPages,
    handleSessionFilter,
    handleSessionsPageChange,
    handleDeleteSession,
  } = sessionHook;

  const handleConfirmDeleteSession = (sessionId: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta partida?')) {
      handleDeleteSession(sessionId);
    }
  };

  // Encontra o nome do jogo a partir da lista de jogos jogados
  const getGameFromList = (gameId: string): UserGame['game'] | undefined => {
      return playedGames.find(g => g.game_id === gameId)?.game;
  }

  // <<< LOG: Verificar os dados recebidos pelo componente >>>
  console.log('[SessionHistoryView] Rendering with sessions received from hook:', gameSessions);
  
  // Verificar especificamente as sessões compartilhadas (is_owner = false)
  const receivedSharedSessions = gameSessions.filter(session => session.is_owner === false);
  console.log('[SessionHistoryView] Sessões compartilhadas recebidas:', 
    receivedSharedSessions.map(s => ({
      id: s.id, 
      game_name: s.game?.name, 
      owner_id: s.owner_id,
      is_owner: s.is_owner,
      tipo_is_owner: typeof s.is_owner
    }))
  );
  
  // Verificar problemas com game_slug em todas as sessões
  console.log('[SessionHistoryView] Verificação do game_slug:', 
    gameSessions.map(s => ({
      id: s.id,
      is_owner: s.is_owner,
      game_slug: s.game?.slug,
      game_obj_slug: s.game?.slug,
      tem_game: !!s.game,
      game_name: s.game?.name
    }))
  );

  // Verificar objetos completos (especialmente para identificar problemas com slug)
  console.log('[SessionHistoryView] Objeto das sessões (3 primeiras para amostra):', 
    gameSessions.slice(0, 3)
  );

  return (
    <>
      {/* Filtros de Sessão */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="session-game-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filtrar por Jogo</label>
            <select
              id="session-game-filter"
              value={sessionsFilters.gameId || ''}
              onChange={(e) => handleSessionFilter({...sessionsFilters, gameId: e.target.value || undefined})}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-gray-100 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
            >
              <option value="">Todos os jogos</option>
              {playedGames.map(game => (
                <option key={game.game_id} value={game.game_id}>
                  {game.game?.name || 'Jogo Desconhecido'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="session-result-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filtrar por Resultado</label>
            <select
              id="session-result-filter"
              value={sessionsFilters.result || ''}
              onChange={(e) => handleSessionFilter({...sessionsFilters, result: e.target.value || undefined})}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-gray-100 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
            >
              <option value="">Todos os resultados</option>
              <option value="vitória">Vitórias</option>
              <option value="derrota">Derrotas</option>
              <option value="empate">Empates</option>
              <option value="cooperativo">Cooperativo</option>
              <option value="incompleto">Incompleto</option>
            </select>
          </div>

          <div>
            <label htmlFor="session-date-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filtrar por Data (Início)</label>
            <input
              type="date"
              id="session-date-filter"
              value={sessionsFilters.startDate ? new Date(sessionsFilters.startDate).toISOString().substring(0, 10) : ''}
              onChange={(e) => handleSessionFilter({...sessionsFilters, startDate: e.target.value ? new Date(e.target.value).toISOString() : undefined})}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-gray-100 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
            />
          </div>
        </div>
      </div>

      {/* Sessões Pendentes */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Sessões Compartilhadas Pendentes</h2>
        <PendingSharedSessions userId={userId} />
      </div>

      {/* Histórico de Partidas */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Histórico de Partidas</h2>
          <button
            onClick={onAddSessionClick} // Usa a prop para abrir o modal geral
            className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white px-3 py-1 rounded text-sm flex items-center"
          >
            <PlusCircle className="h-4 w-4 mr-1" />
            Registrar Partida
          </button>
        </div>

        {sessionsLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 dark:border-indigo-400 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-300">Carregando histórico de partidas...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/70 p-4 rounded-lg mb-4">
             <div className="flex">
               <div className="flex-shrink-0">
                 <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-500" />
               </div>
               <div className="ml-3">
                 <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                   Erro ao carregar histórico
                 </h3>
                 <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                   <p>{error}</p>
                 </div>
               </div>
             </div>
          </div>
        ) : gameSessions.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg text-center">
            <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-2">Nenhum registro de partida</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
                {sessionsFilters.gameId || sessionsFilters.result || sessionsFilters.startDate 
                    ? 'Nenhuma partida encontrada com os filtros aplicados.' 
                    : 'Você ainda não registrou nenhuma partida. Use o botão acima ou na lista de jogos.'}
                </p>
            {/* Botão para limpar filtros poderia ser adicionado aqui */}
          </div>
        ) : (
          <>
            {/* Tabela Desktop */} 
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-slate-700 overflow-hidden border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="dark:bg-gray-750">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Jogo</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duração</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Jogadores</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Resultado</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pontuação</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {gameSessions.map(session => {
                      // <<< LOG: Verificar cada sessão antes de renderizar a linha >>>
                      console.log('[SessionHistoryView] Mapping Desktop session:', 
                        { id: session.id, name: session.game?.name, is_owner: session.is_owner, game_slug: session.game?.slug });
                      return (
                        <tr key={session.id} className={`${!session.is_owner ? 'bg-blue-50 dark:bg-blue-900/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                          {/* Coluna Jogo */}
                          <td className="px-6 py-4 whitespace-nowrap">
                             <div className="flex items-center">
                               <div className="flex-shrink-0 h-10 w-10">
                                 {session.game?.thumb_url ? (
                                   <img 
                                     src={session.game.thumb_url.startsWith("http") ? session.game.thumb_url : (session.game.image_url || '/placeholder-game.png')} 
                                     alt={session.game.name}
                                     className="h-10 w-10 rounded-sm object-cover" 
                                     onError={(e) => {
                                       (e.target as HTMLImageElement).onerror = null;
                                       (e.target as HTMLImageElement).src = session.game?.image_url || '/placeholder-game.png';
                                     }}
                                   />
                                 ) : session.game?.image_url ? (
                                   <img 
                                     src={session.game.image_url} 
                                     alt={session.game.name}
                                     className="h-10 w-10 rounded-sm object-cover" 
                                     onError={(e) => {
                                       (e.target as HTMLImageElement).onerror = null;
                                       (e.target as HTMLImageElement).src = '/placeholder-game.png';
                                     }}
                                   />
                                 ) : (
                                   <div className="h-10 w-10 rounded-sm bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-500">
                                     <Swords className="h-5 w-5" />
                                   </div>
                                 )}
                               </div>
                               <div className="ml-4">
                                  <Link 
                                    to={session.game && session.game.slug ? `/games/${session.game.slug}` : '#'} 
                                    className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400"
                                  >
                                    {session.game?.name || 'Jogo Desconhecido'}
                                  </Link>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(session.played_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </div>
                               </div>
                             </div>
                          </td>
                          {/* Coluna Data */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(session.played_at).toLocaleDateString('pt-BR')}
                          </td>
                          {/* Coluna Duração */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {session.duration_minutes ? `${session.duration_minutes} min` : '-'}
                          </td>
                          {/* Coluna Jogadores */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {session.player_count ? `${session.player_count}` : '-'}
                            {session.players && Array.isArray(session.players) && session.players.length > 0 && (
                              <div className="text-xs truncate max-w-[100px]" title={session.players.map(p => p.name).join(', ')}>
                                {session.players.map(p => p.name).join(', ')}
                              </div>
                            )}
                          </td>
                          {/* Coluna Resultado */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                              ${session.result === 'vitória' ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200' : 
                                session.result === 'derrota' ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200' : 
                                session.result === 'empate' ? 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'}`}>
                              {session.result || '-'}
                            </span>
                          </td>
                          {/* Coluna Pontuação */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {session.score !== null ? session.score : '-'}
                            {session.opponent_score !== null && ` (vs ${session.opponent_score})`}
                          </td>
                          {/* Coluna Ações */}
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-1">
                              <Link
                                to={`/sessions/${session.id}`}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                                title="Ver Detalhes da Partida"
                              >
                                <Swords className="h-4 w-4" />
                              </Link>
                              {session.is_owner && (
                                <>
                                  <button
                                    onClick={() => onEditSession(session, session.game as Game | undefined)} // Asserção de tipo
                                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1 rounded-md hover:bg-indigo-50 dark:hover:bg-gray-700"
                                    title="Editar Partida"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  {/* <button
                                    onClick={() => { alert(\`Compartilhar sessão ID: ${session.id}\`); }}
                                    className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50"
                                    title="Compartilhar Partida"
                                  >
                                    <Share2 className="h-4 w-4" />
                                  </button> */}
                                  <button
                                    onClick={() => handleConfirmDeleteSession(session.id)}
                                    className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded-md hover:bg-red-50 dark:hover:bg-gray-700"
                                    title="Excluir Partida"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              {!session.is_owner && (
                                  <Link
                                      to={session.owner_username ? `/profile/${session.owner_username}` : '#'}
                                      className="text-xs text-gray-400 dark:text-gray-500 hover:underline"
                                      title={session.owner_username ? `Ver perfil de ${session.owner_username}` : (session.owner_id ? `Username do proprietário não disponível (ID: ${session.owner_id})` : 'Proprietário desconhecido')}
                                  >
                                      Ver Dono
                                  </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
            </div>

            {/* Cards Mobile */}
            <div className="md:hidden space-y-4">
              {gameSessions.map(session => (
                <div key={session.id} className={`bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 ${!session.is_owner ? 'border-blue-300 dark:border-blue-700' : ''}`}>
                  <div className="p-4">
                    <div className="flex items-start space-x-3">
                       <div className="flex-shrink-0">
                        {session.game?.thumb_url ? (
                            <img className="h-16 w-16 rounded-md object-cover" src={session.game.thumb_url.startsWith("http") ? session.game.thumb_url : (session.game.image_url || '/placeholder-game.png')} alt={session.game?.name} 
                              onError={(e) => { (e.target as HTMLImageElement).src = session.game?.image_url || '/placeholder-game.png'; }}
                            />
                        ) : session.game?.image_url ? (
                            <img className="h-16 w-16 rounded-md object-cover" src={session.game.image_url} alt={session.game?.name}
                              onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder-game.png'; }}
                            />
                        ) : (
                            <div className="h-16 w-16 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500">
                                <Swords className="h-8 w-8" />
                            </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link to={session.game && session.game.slug ? `/games/${session.game.slug}` : '#'} className="text-base font-medium text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400 truncate block">
                          {session.game?.name || 'Jogo Desconhecido'}
                        </Link>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(session.played_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-2 gap-y-0.5">
                            {session.duration_minutes && <span className="flex items-center"><Clock className="h-3 w-3 mr-0.5" /> {session.duration_minutes} min</span>}
                            {session.player_count && <span className="flex items-center"><Users className="h-3 w-3 mr-0.5" /> {session.player_count}</span>}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                       <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Resultado:</span>
                          <span className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full 
                            ${session.result === 'vitória' ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200' : 
                              session.result === 'derrota' ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200' : 
                              session.result === 'empate' ? 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'}`}>
                            {session.result || 'Não registrado'}
                          </span>
                       </div>
                       <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Pontuação:</span>
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {session.score !== null ? session.score : '-'}
                            {session.opponent_score !== null && ` (vs ${session.opponent_score})`}
                          </span>
                       </div>
                       {session.players && Array.isArray(session.players) && session.players.length > 0 && (
                         <div className="mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Jogou com:</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 truncate">{session.players.map(p => p.name).join(', ')}</p>
                         </div>
                       )}
                    </div>
                  </div>
                  <div className={`px-4 py-2 border-t border-gray-200 dark:border-gray-700 ${!session.is_owner ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-gray-50 dark:bg-gray-700/60'}`}>
                    <div className="flex justify-end space-x-2">
                      <Link
                        to={`/sessions/${session.id}`}
                        className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 text-xs flex items-center"
                        title="Ver Detalhes"
                      >
                        <BookOpen className="h-4 w-4 mr-1" /> Detalhes
                      </Link>
                      {session.is_owner ? (
                        <>
                          <button
                            onClick={() => onEditSession(session, session.game as Game | undefined)} // Asserção de tipo
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-gray-600 text-xs flex items-center"
                            title="Editar Partida"
                          >
                            <Edit className="h-4 w-4 mr-1" /> Editar
                          </button>
                          <button
                            onClick={() => handleConfirmDeleteSession(session.id)}
                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-gray-600 text-xs flex items-center"
                            title="Excluir Partida"
                          >
                            <Trash2 className="h-4 w-4 mr-1" /> Excluir
                          </button>
                        </>
                      ) : (
                         <Link
                              to={session.owner_username ? `/profile/${session.owner_username}` : '#'}
                              className="text-xs text-gray-500 dark:text-gray-400 hover:underline p-1.5"
                              title={session.owner_username ? `Ver perfil de ${session.owner_username}` : (session.owner_id ? `Username do proprietário não disponível (ID: ${session.owner_id})` : 'Proprietário desconhecido')}
                          >
                              {session.owner_username ? `Registrada por ${session.owner_username}` : (session.owner_id ? 'Partida de Outro (Ver Perfil)' : 'Proprietário Desconhecido')}
                          </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginação */}
            {sessionsTotalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 px-2">
                <p className="text-sm text-gray-700 dark:text-gray-400">
                  Página <span className="font-medium">{sessionsPage}</span> de <span className="font-medium">{sessionsTotalPages}</span>
                </p>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handleSessionsPageChange(sessionsPage - 1)}
                    disabled={sessionsPage === 1 || sessionsLoading}
                    className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium transition-colors
                      ${sessionsPage === 1 || sessionsLoading 
                        ? 'bg-gray-100 dark:bg-gray-750 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                        : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handleSessionsPageChange(sessionsPage + 1)}
                    disabled={sessionsPage >= sessionsTotalPages || sessionsLoading}
                    className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium transition-colors
                    ${sessionsPage >= sessionsTotalPages || sessionsLoading 
                        ? 'bg-gray-100 dark:bg-gray-750 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                        : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
                  >
                    Próxima
                  </button>
                </nav>
              </div>
            )}
            <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">Total de {sessionsCount} partidas registradas.</p>
          </>
        )}
      </div>
    </>
  );
};

export default SessionHistoryView; 